import { Spinner } from '@ibsys/kit';
import { FC, MouseEvent, ReactNode } from 'react';
import { PlayIcon, SquareStopIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/shared/ui';
import { PhrasePlaybackTarget, usePhrasePlayback } from '../model/usePhrasePlayback';

type Props = PhrasePlaybackTarget & {
  /**
   * Достижима ли кнопка обходом с клавиатуры (по умолчанию да). Список, где обход идёт
   * по полям строк, выключает это: между полями соседних строк кнопке места нет.
   */
  isTabbable?: boolean;
  /** Вид предложения слушать; по умолчанию — обычное прослушивание реплики */
  playIcon?: ReactNode;
  /** Подпись предложения слушать; по умолчанию — обычное прослушивание реплики */
  playLabel?: string;
};

/**
 * Кнопка прослушивания реплики: запускает запись её диалога и обрывает её на конце реплики.
 * Границы звучания задаёт вызывающий — ими же отличаются два прослушивания одной строки,
 * сама реплика и она же с запасом по краям.
 *
 * Различает три состояния: предложение слушать, получение записи (от нажатия и до первого
 * звука, включая ожидание записи, заказанной у источника) и звучание. Во время получения
 * кнопка остаётся нажимаемой — повторным нажатием ожидание прекращается.
 */
export const PhrasePlayButton: FC<Props> = ({ isTabbable = true, playIcon, playLabel, ...target }) => {
  const { t } = useTranslation('dialogs');
  const { isPlaying, isLoading, toggle } = usePhrasePlayback(target);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Клик по кнопке не открывает диалог строки
    event.stopPropagation();
    toggle();
  };

  const getLabel = () => {
    if (isLoading) return t('phraseSearch.cancelLoading');
    if (isPlaying) return t('phraseSearch.stop');
    return playLabel ?? t('phraseSearch.play');
  };

  // Спиннер как иконка, а не `isLoading` кнопки: та на время загрузки перестаёт нажиматься,
  // а ожидание записи нужно прекращать повторным нажатием
  const getIcon = () => {
    if (isLoading) return <Spinner className="size-3.5" />;
    if (isPlaying) return <SquareStopIcon className="size-3.5" />;
    return playIcon ?? <PlayIcon className="size-3.5" />;
  };

  const label = getLabel();

  return (
    <IconButton
      size="icon"
      className="size-6"
      label={label}
      tabIndex={isTabbable ? undefined : -1}
      icon={getIcon()}
      onClick={handleClick}
    />
  );
};
